import { Button, cn } from '@parcel/ui';
import { ArrowRight, ArrowUpRight, Users } from 'lucide-react';
import { normalizeExternalUrl } from '../../lib/external-url.js';
import type { FeedItem, FeedRow } from '../../lib/feed.js';
import { logoDomain } from '../../lib/logo.js';
import { safeHref } from '../../lib/markdown.js';
import { PRIORITY_DOT_CLASS } from '../../lib/signal-labels.js';
import type { ApiSignalType } from '../../lib/signals.js';
import { getSignalMeta } from '../entity-drawer/signal-feed.js';
import { Hint } from '../hint.js';
import { shortDate, statusLabel, whyNowText } from './feed-format.js';
import { SignalChip } from './feed-row.js';

/** Signals shown in the open row; the record holds the rest. */
const RECENT_SHOWN = 2;

/** The open row: why it is news, a way into the record, its facts, and its two newest signals. */
export function FeedRowExpanded({
  row,
  onOpen,
  onAskSpec,
  onReviewContacts,
}: {
  row: FeedRow;
  onOpen: () => void;
  /** Absent where Spec is not available. */
  onAskSpec?: () => void;
  onReviewContacts?: () => void;
}) {
  const isAccount = row.dataset === 'account';
  const facts: [string, string][] = isAccount
    ? [
        ['Type', row.account_type ?? '—'],
        ['Projects', statusLabel(row)],
        ['Followed since', shortDate(row.followed_at)],
        ['Recent news', row.item_count.toLocaleString()],
      ]
    : [
        ['Stage', statusLabel(row)],
        [
          'Accounts',
          row.via_accounts
            .map((account) => account.name)
            .filter(Boolean)
            .join(', ') || '—',
        ],
        ['Followed since', shortDate(row.followed_at)],
        ['Recent news', row.item_count.toLocaleString()],
      ];
  const signals = row.items.filter((item) => item.kind === 'signal');
  const recent = (signals.length > 0 ? signals : row.items).slice(0, RECENT_SHOWN);
  const more = row.item_count - recent.length;
  const outward = isAccount ? row.website : row.latest_source?.base_url;
  const outwardLabel = logoDomain(outward);
  return (
    <div className="border-t px-4 pb-4 pt-4">
      <div className="grid grid-cols-[minmax(0,1fr)_180px] gap-6">
        <div className="min-w-0">
          <div className="rounded-lg bg-brand-muted/60 px-4 py-3">
            <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-brand">
              What's the latest
            </p>
            <p className="whitespace-pre-line text-sm leading-relaxed">
              {row.why_now ?? whyNowText(row)}
            </p>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {isAccount && onReviewContacts ? (
              <Hint label="See who to contact at this account">
                <Button size="sm" onClick={onReviewContacts}>
                  <Users className="mr-1.5 size-3.5" aria-hidden />
                  Review contacts
                </Button>
              </Hint>
            ) : null}
            <Hint label={isAccount ? 'Open the full account' : 'Open the full project'}>
              <Button size="sm" variant={isAccount ? 'outline' : 'default'} onClick={onOpen}>
                {isAccount ? 'Open account' : 'Open project'}
                <ArrowRight className="ml-1.5 size-3.5" aria-hidden />
              </Button>
            </Hint>
            {onAskSpec ? (
              <Hint label={`Ask Spec about this ${isAccount ? 'account' : 'project'}`}>
                <Button size="sm" variant="outline" onClick={onAskSpec}>
                  Ask Spec
                </Button>
              </Hint>
            ) : null}
            {outward && outwardLabel ? (
              <Hint label={`Open ${outwardLabel}`}>
                <a
                  href={normalizeExternalUrl(outward)}
                  target="_blank"
                  rel="noreferrer"
                  className="ml-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                >
                  {outwardLabel}
                  <ArrowUpRight className="size-3" aria-hidden />
                </a>
              </Hint>
            ) : null}
          </div>
        </div>
        <dl className="border-l pl-5">
          {facts.map(([label, value]) => (
            <div key={label} className="mb-2.5 last:mb-0">
              <dt className="text-xs text-muted-foreground">{label}</dt>
              <dd className="text-sm">{value}</dd>
            </div>
          ))}
        </dl>
      </div>
      <div className="mt-4 border-t pt-4">
        <p className="mb-3 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          {signals.length > 0 ? 'Recent signals' : 'Recent documents'}
        </p>
        <ul className="space-y-3">
          {recent.map((item) => (
            <RecentItem key={item.id} item={item} showProject={isAccount} />
          ))}
        </ul>
        {more > 0 ? (
          <div className={cn(RECENT_GRID, 'mt-3')}>
            <button
              type="button"
              onClick={onOpen}
              className="col-start-3 justify-self-start text-xs font-medium text-brand hover:underline"
            >
              {more.toLocaleString()} more on this {isAccount ? 'account' : 'project'} →
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** Date, priority dot, then the chip and one line: the grid the "+ N more" link lines up under. */
const RECENT_GRID = 'grid grid-cols-[56px_8px_minmax(0,1fr)_24px] gap-x-3';

function RecentItem({ item, showProject }: { item: FeedItem; showProject: boolean }) {
  const isSignal = item.kind === 'signal';
  const label = isSignal
    ? getSignalMeta(item.signal_type as ApiSignalType).label
    : (item.document_type ?? 'Document');
  const title = isSignal ? (item.summary ?? label) : (item.label ?? 'New document');
  const dot = isSignal ? PRIORITY_DOT_CLASS[item.priority] : 'bg-muted-foreground/40';
  const found = isSignal
    ? item.sources.find((candidate) => candidate.source_url && safeHref(candidate.source_url))
    : { source_url: item.source_url, source_display_name: item.label };
  const href = found?.source_url ? (safeHref(found.source_url) ?? undefined) : undefined;
  const sourceName = found?.source_display_name ?? 'the source';
  return (
    <li className={cn(RECENT_GRID, 'items-start')}>
      <span className="pt-0.5 text-right text-xs text-muted-foreground">
        {shortDate(item.discovered_at)}
      </span>
      <span className={cn('mt-[7px] size-1.5 rounded-full', dot)} aria-hidden />
      <span className="min-w-0">
        <span className="flex min-w-0 items-center gap-2">
          <SignalChip label={label} dot={isSignal ? PRIORITY_DOT_CLASS[item.priority] : null} />
          {showProject && item.project_name ? (
            <span className="truncate text-xs text-muted-foreground">{item.project_name}</span>
          ) : null}
        </span>
        <span className="mt-1 line-clamp-2 max-w-[min(36rem,70%)] text-sm leading-relaxed">
          {title}
        </span>
      </span>
      {href ? (
        <Hint label={`Open on ${sourceName}`}>
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            aria-label={`Open on ${sourceName}`}
            className="flex size-6 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <ArrowUpRight className="size-3.5" aria-hidden />
          </a>
        </Hint>
      ) : (
        <span />
      )}
    </li>
  );
}
